import React, { useCallback, useEffect, useState } from 'react';
import { voiceInfraApi } from './voiceInfraApi';

type Row = Record<string, unknown>;

function Panel({
  title,
  children,
  action,
}: {
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-enterprise-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-enterprise-900">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function StatusPill({ value }: { value?: string | null }) {
  const v = (value || 'unknown').toLowerCase();
  const color =
    v === 'healthy' || v === 'active' || v === 'ok'
      ? 'bg-emerald-50 text-emerald-700'
      : v === 'warning' || v === 'degraded'
        ? 'bg-amber-50 text-amber-700'
        : v === 'critical' || v === 'offline' || v === 'open'
          ? 'bg-rose-50 text-rose-700'
          : 'bg-slate-100 text-slate-600';
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${color}`}>{v}</span>
  );
}

function useLoad<T>(loader: () => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res: any = await loader();
      setData(res?.data ?? res);
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [loader]);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { data, error, loading, reload };
}

export const VoiceInfraOverviewPage: React.FC = () => {
  const { data, error, loading, reload } = useLoad(() => voiceInfraApi.health());
  const health = (data as any) || {};
  return (
    <Panel
      title="Health overview"
      action={
        <button
          type="button"
          onClick={() => void reload()}
          className="rounded-lg bg-enterprise-900 px-3 py-1.5 text-sm text-white"
        >
          Refresh
        </button>
      }
    >
      {loading && <p className="text-sm text-enterprise-500">Loading…</p>}
      {error && <p className="text-sm text-rose-600">{error}</p>}
      {!loading && !error && (
        <div className="grid gap-4 md:grid-cols-3">
          {[
            ['Providers', health.providers || []],
            ['SIP Trunks', health.sip_trunks || []],
            ['Carriers', health.carriers || []],
          ].map(([label, rows]) => (
            <div key={String(label)} className="rounded-xl border border-enterprise-100 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-enterprise-500">
                {String(label)}
              </p>
              <ul className="mt-3 space-y-2">
                {(rows as Row[]).map((r, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <span className="font-medium text-enterprise-800">
                      {String(r.name || r.code || r.id || '—')}
                    </span>
                    <StatusPill value={String(r.health || r.status || '')} />
                  </li>
                ))}
                {(rows as Row[]).length === 0 && (
                  <li className="text-sm text-enterprise-500">No records yet</li>
                )}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
};

export const VoiceInfraProvidersPage: React.FC = () => {
  const { data, error, loading, reload } = useLoad(() => voiceInfraApi.providers());
  const rows = ((data as any) || []) as Row[];
  return (
    <Panel title="Providers" action={<button type="button" onClick={() => void reload()} className="text-sm text-sky-700">Reload</button>}>
      {loading && <p className="text-sm text-enterprise-500">Loading…</p>}
      {error && <p className="text-sm text-rose-600">{error}</p>}
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="text-xs uppercase text-enterprise-500">
            <tr>
              <th className="py-2 pr-4">Code</th>
              <th className="py-2 pr-4">Name</th>
              <th className="py-2 pr-4">Priority</th>
              <th className="py-2 pr-4">Enabled</th>
              <th className="py-2 pr-4">Health</th>
              <th className="py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={String(r.id || r.code)} className="border-t border-enterprise-100">
                <td className="py-2 pr-4 font-mono text-xs">{String(r.code)}</td>
                <td className="py-2 pr-4">{String(r.name)}</td>
                <td className="py-2 pr-4">{String(r.priority)}</td>
                <td className="py-2 pr-4">{r.enabled ? 'Yes' : 'No'}</td>
                <td className="py-2 pr-4"><StatusPill value={String(r.health)} /></td>
                <td className="py-2"><StatusPill value={String(r.status)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-enterprise-500">
        Pluggable providers (LiveKit, Retell). Emergency disable is managed via provider status.
      </p>
    </Panel>
  );
};

export const VoiceInfraTrunksPage: React.FC = () => {
  const { data, error, loading, reload } = useLoad(() => voiceInfraApi.trunks());
  const [form, setForm] = useState({
    name: '',
    provider_id: '',
    external_trunk_id: '',
    country: '',
    region: '',
  });
  const rows = ((data as any) || []) as Row[];

  const create = async () => {
    await voiceInfraApi.createTrunk({
      ...form,
      outbound_enabled: true,
      inbound_enabled: true,
      enabled: true,
    });
    setForm({ name: '', provider_id: '', external_trunk_id: '', country: '', region: '' });
    await reload();
  };

  return (
    <Panel title="SIP Trunks">
      {loading && <p className="text-sm text-enterprise-500">Loading…</p>}
      {error && <p className="text-sm text-rose-600">{error}</p>}
      <div className="mb-4 grid gap-2 md:grid-cols-5">
        {(['name', 'provider_id', 'external_trunk_id', 'country', 'region'] as const).map((k) => (
          <input
            key={k}
            className="rounded-lg border border-enterprise-200 px-3 py-2 text-sm"
            placeholder={k}
            value={form[k]}
            onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
          />
        ))}
      </div>
      <button
        type="button"
        onClick={() => void create()}
        className="mb-4 rounded-lg bg-sky-700 px-3 py-1.5 text-sm text-white"
      >
        Create trunk
      </button>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="text-xs uppercase text-enterprise-500">
            <tr>
              <th className="py-2 pr-4">Name</th>
              <th className="py-2 pr-4">External trunk</th>
              <th className="py-2 pr-4">Country</th>
              <th className="py-2 pr-4">Health</th>
              <th className="py-2">Circuit</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={String(r.id)} className="border-t border-enterprise-100">
                <td className="py-2 pr-4">{String(r.name)}</td>
                <td className="py-2 pr-4 font-mono text-xs">{String(r.external_trunk_id || '—')}</td>
                <td className="py-2 pr-4">{String(r.country || '—')}</td>
                <td className="py-2 pr-4"><StatusPill value={String(r.health)} /></td>
                <td className="py-2"><StatusPill value={String(r.circuit_breaker_state)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-enterprise-500">SIP credentials are never returned to CRM clients.</p>
    </Panel>
  );
};

export const VoiceInfraPhonesPage: React.FC = () => {
  const { data, error, loading, reload } = useLoad(() => voiceInfraApi.phones());
  const rows = ((data as any) || []) as Row[];
  return (
    <Panel title="Phone Number Registry (optional overlay)">
      <p className="mb-3 text-sm text-enterprise-600">
        Day-to-day numbers are registered and assigned on the <strong>workspace Phone Numbers</strong> page.
        AI Calling resolves <code className="text-xs">outbound_agent_id</code> from that table. This Admin
        registry is an optional enterprise overlay only.
      </p>
      {loading && <p className="text-sm text-enterprise-500">Loading…</p>}
      {error && <p className="text-sm text-rose-600">{error}</p>}
      <button type="button" onClick={() => void reload()} className="mb-3 text-sm text-sky-700">
        Reload overlay
      </button>
      <table className="min-w-full text-left text-sm">
        <thead className="text-xs uppercase text-enterprise-500">
          <tr>
            <th className="py-2 pr-4">Number</th>
            <th className="py-2 pr-4">Workspace</th>
            <th className="py-2 pr-4">AI Employee</th>
            <th className="py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={String(r.id)} className="border-t border-enterprise-100">
              <td className="py-2 pr-4 font-mono text-xs">{String(r.e164)}</td>
              <td className="py-2 pr-4 font-mono text-xs">{String(r.workspace_id || '—')}</td>
              <td className="py-2 pr-4 font-mono text-xs">{String(r.assigned_ai_employee_id || '—')}</td>
              <td className="py-2"><StatusPill value={String(r.status)} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
};

export const VoiceInfraCarriersPage: React.FC = () => {
  const { data, error, loading } = useLoad(() => voiceInfraApi.carriers());
  const rows = ((data as any) || []) as Row[];
  return (
    <Panel title="Carrier Routing">
      {loading && <p className="text-sm text-enterprise-500">Loading…</p>}
      {error && <p className="text-sm text-rose-600">{error}</p>}
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={String(r.id)} className="flex items-center justify-between rounded-lg border border-enterprise-100 px-3 py-2 text-sm">
            <div>
              <p className="font-medium">{String(r.name)}</p>
              <p className="text-xs text-enterprise-500">
                {String(r.code)} · {(r.supported_countries as string[] | undefined)?.join(', ') || '—'}
              </p>
            </div>
            <StatusPill value={String(r.health)} />
          </li>
        ))}
      </ul>
    </Panel>
  );
};

export const VoiceInfraGeoRoutesPage: React.FC = () => {
  const { data, error, loading, reload } = useLoad(() => voiceInfraApi.geoRoutes());
  const [form, setForm] = useState({ name: '', country_prefix: '', country_code: '', sip_trunk_id: '' });
  const rows = ((data as any) || []) as Row[];
  return (
    <Panel title="Geographic Routing">
      {loading && <p className="text-sm text-enterprise-500">Loading…</p>}
      {error && <p className="text-sm text-rose-600">{error}</p>}
      <div className="mb-4 grid gap-2 md:grid-cols-4">
        {(['name', 'country_prefix', 'country_code', 'sip_trunk_id'] as const).map((k) => (
          <input
            key={k}
            className="rounded-lg border px-3 py-2 text-sm"
            placeholder={k}
            value={form[k]}
            onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
          />
        ))}
      </div>
      <button
        type="button"
        className="mb-4 rounded-lg bg-sky-700 px-3 py-1.5 text-sm text-white"
        onClick={() => void voiceInfraApi.createGeoRoute({ ...form, enabled: true }).then(() => reload())}
      >
        Add route
      </button>
      <table className="min-w-full text-left text-sm">
        <thead className="text-xs uppercase text-enterprise-500">
          <tr>
            <th className="py-2 pr-4">Name</th>
            <th className="py-2 pr-4">Prefix</th>
            <th className="py-2 pr-4">Country</th>
            <th className="py-2">Trunk</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={String(r.id)} className="border-t border-enterprise-100">
              <td className="py-2 pr-4">{String(r.name)}</td>
              <td className="py-2 pr-4 font-mono text-xs">{String(r.country_prefix)}</td>
              <td className="py-2 pr-4">{String(r.country_code || '—')}</td>
              <td className="py-2 font-mono text-xs">{String(r.sip_trunk_id || '—')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
};

export const VoiceInfraHealthPage: React.FC = () => <VoiceInfraOverviewPage />;

export const VoiceInfraFailoverPage: React.FC = () => {
  const { data, error, loading } = useLoad(() => voiceInfraApi.failover());
  const rows = ((data as any) || []) as Row[];
  return (
    <Panel title="Failover Rules">
      {loading && <p className="text-sm text-enterprise-500">Loading…</p>}
      {error && <p className="text-sm text-rose-600">{error}</p>}
      <p className="mb-3 text-sm text-enterprise-600">
        Default policy is <strong>no failover</strong>. Silent provider switching is forbidden.
      </p>
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={String(r.id)} className="rounded-lg border border-enterprise-100 px-3 py-2 text-sm">
            <p className="font-medium">{String(r.name)}</p>
            <p className="text-xs text-enterprise-500">
              policy={String(r.policy)} · primary={String(r.primary_provider_code)} · enabled=
              {String(r.enabled)}
            </p>
          </li>
        ))}
      </ul>
    </Panel>
  );
};

export const VoiceInfraDiagnosticsPage: React.FC = () => {
  const { data, error, loading, reload } = useLoad(() => voiceInfraApi.diagnostics());
  const [dest, setDest] = useState('+919888864010');
  const [workspaceId, setWorkspaceId] = useState('');
  const [aiEmployeeId, setAiEmployeeId] = useState('');
  const [preview, setPreview] = useState<any>(null);
  const rows = ((data as any) || []) as Row[];

  return (
    <div className="space-y-4">
      <Panel title="Route preview (no dial)">
        <div className="mb-3 grid gap-2 md:grid-cols-3">
          <input className="rounded-lg border px-3 py-2 text-sm" placeholder="workspace_id" value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)} />
          <input className="rounded-lg border px-3 py-2 text-sm" placeholder="ai_employee_id" value={aiEmployeeId} onChange={(e) => setAiEmployeeId(e.target.value)} />
          <input className="rounded-lg border px-3 py-2 text-sm" placeholder="destination" value={dest} onChange={(e) => setDest(e.target.value)} />
        </div>
        <button
          type="button"
          className="rounded-lg bg-enterprise-900 px-3 py-1.5 text-sm text-white"
          onClick={() =>
            void voiceInfraApi
              .resolve({
                workspace_id: workspaceId,
                ai_employee_id: aiEmployeeId,
                destination_number: dest,
                validate: true,
              })
              .then((r: any) => setPreview(r?.data ?? r))
          }
        >
          Resolve + validate
        </button>
        {preview && (
          <pre className="mt-3 max-h-80 overflow-auto rounded-lg bg-slate-950 p-3 text-xs text-slate-100">
            {JSON.stringify(preview, null, 2)}
          </pre>
        )}
      </Panel>
      <Panel title="Recent diagnostics" action={<button type="button" onClick={() => void reload()} className="text-sm text-sky-700">Reload</button>}>
        {loading && <p className="text-sm text-enterprise-500">Loading…</p>}
        {error && <p className="text-sm text-rose-600">{error}</p>}
        <ul className="space-y-2">
          {rows.map((r) => (
            <li key={String(r.id)} className="rounded-lg border border-enterprise-100 px-3 py-2 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-mono text-xs">{String(r.session_id || r.id)}</span>
                <StatusPill value={((r.validation_result as any)?.ok ? 'healthy' : 'critical') as string} />
              </div>
              <p className="mt-1 text-xs text-enterprise-500">
                {String(r.provider_code)} · {String(r.carrier_code || '—')} · {String(r.phone_number_e164 || '—')} ·{' '}
                {String(r.country_prefix || '—')}
              </p>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
};

export const VoiceInfraProvisioningPage: React.FC = () => (
  <Panel title="Provisioning">
    <ol className="list-decimal space-y-2 pl-5 text-sm text-enterprise-700">
      <li>Purchase / import numbers via the workspace Phone Numbers page (CRM).</li>
      <li>Assign the number to an AI Employee (outbound) on that workspace page.</li>
      <li>Register LiveKit as the AI media provider (rooms / worker) — not as a carrier.</li>
      <li>
        PSTN always goes through Asterisk: lk.swsagent.com (Telnyx) or pbx.swsagent.com (Airtel).
      </li>
      <li>Configure carrier + geographic routing here; Asterisk owns registration and failover.</li>
      <li>All workspaces default to <strong>Retell</strong>; opt into SWS AI Calling per workspace under Voice Platforms.</li>
      <li>Use Diagnostics → Resolve to confirm validation before first dial.</li>
    </ol>
    <p className="mt-4 text-xs text-enterprise-500">
      Phone numbers are owned by workspaces. Admin Voice Infrastructure manages AI media providers,
      Asterisk/carrier routing — not day-to-day DID assignment. Human Calling stays untouched.
    </p>
  </Panel>
);
